import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import {
  AnalyticsDashboardKpis,
  AnalyticsTrendEvent,
  AnalyticsSpcReport,
  SpcDataPoint,
} from './types';
import { analyticsService } from './analyticsService';
import { AnalyticsMetricsCards } from './components/AnalyticsMetricsCards';
import { AnalysisPage } from '../analysis/AnalysisPage';
import { performanceService } from '../safety/performance/performanceService';
import { SpiStatisticsResponse } from '../safety/performance/types';

interface AnalyticsPageProps {
  initialTab?: 'dashboards' | 'trends' | 'spi_stats';
}

export const AnalyticsPage: React.FC<AnalyticsPageProps> = ({ initialTab }) => {
  const [activeTab, setActiveTab] = useState<'dashboards' | 'trends' | 'spi_stats'>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sub = urlParams.get('subTab') || urlParams.get('tabSub');
    if (sub && (sub === 'spi' || sub === 'spi_stats' || sub === 'performance')) {
      return 'spi_stats';
    }
    if (sub && ['dashboards', 'trends', 'spi_stats'].includes(sub)) {
      return sub as 'dashboards' | 'trends' | 'spi_stats';
    }
    return initialTab || 'dashboards';
  });

  const [kpis, setKpis] = useState<AnalyticsDashboardKpis | null>(null);
  const [trendEvents, setTrendEvents] = useState<AnalyticsTrendEvent[]>([]);

  // Real SPC 3-Sigma state from live database occurrences
  const [spcReport, setSpcReport] = useState<AnalyticsSpcReport | null>(null);
  const [spcMetricCode, setSpcMetricCode] = useState<string>('OCCURRENCES_ALL');
  const [spcMonths, setSpcMonths] = useState<number>(18);
  const [spcLoading, setSpcLoading] = useState<boolean>(false);
  const [spiStats, setSpiStats] = useState<SpiStatisticsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [kpiData, trdData, spcData, spiData] = await Promise.all([
        analyticsService.getDashboardKpis(),
        analyticsService.getTrendEvents(),
        analyticsService.getSpcAnalysis(spcMetricCode, spcMonths),
        performanceService.getSpiStatistics().catch(() => null),
      ]);
      setKpis(kpiData);
      setTrendEvents(trdData);
      setSpcReport(spcData);
      setSpiStats(spiData);
    } catch (err) {
      console.error('Error loading analytics data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSpcMetricChange = async (code: string) => {
    setSpcMetricCode(code);
    try {
      setSpcLoading(true);
      const rep = await analyticsService.getSpcAnalysis(code, spcMonths);
      setSpcReport(rep);
    } catch (err) {
      console.error('Error fetching SPC analysis:', err);
    } finally {
      setSpcLoading(false);
    }
  };

  const handleSpcMonthsChange = async (months: number) => {
    setSpcMonths(months);
    try {
      setSpcLoading(true);
      const rep = await analyticsService.getSpcAnalysis(spcMetricCode, months);
      setSpcReport(rep);
    } catch (err) {
      console.error('Error fetching SPC analysis:', err);
    } finally {
      setSpcLoading(false);
    }
  };

  const handleReviewTrend = async (id: string, status: any) => {
    await analyticsService.reviewTrendEvent(id, {
      status,
      reviewed_by: 'Safety Manager',
      review_notes: 'Reviewed and incorporated into active safety mitigation plan.',
    });
    await loadAll();
  };

  // Dynamic SPC 3-Sigma data points calculated from live database occurrences
  const spcChartData = spcReport?.data_points || [];

  return (
    <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '0 24px' }}>
      {/* Top Banner with Freshness Indicator */}
      <div className="page-header-banner">
        <div className="badge">SAFETY INTELLIGENCE & ANALYTICS</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Safety Analytics & Statistical Intelligence</h1>
            <p>
              Custom visual dashboards, operational occurrence metrics, and dynamic 3-sigma statistical process control (SPC) charts.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span className="badge-pill badge-neutral" style={{ fontSize: '12px', padding: '6px 12px' }}>
              ⏱️ Real-time Calculated: {kpis?.latest_watermark ? kpis.latest_watermark.slice(0, 19).replace('T', ' ') : 'Live Database Telemetry'}
            </span>
            <button
              onClick={loadAll}
              disabled={loading}
              className="btn-secondary"
              style={{ padding: '6px 12px', fontSize: '12px', width: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}
              title="Recalculate all metrics dynamically from database records"
            >
              🔄 {loading ? 'Calculating...' : 'Recalculate Live'}
            </button>
          </div>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <AnalyticsMetricsCards
        kpis={kpis}
        spiStats={spiStats}
        onSelectSpiTab={() => setActiveTab('spi_stats')}
      />

      {/* Navigation Sub-Tabs: Dashboards, Trends, and SPI Analytics */}
      <div className="nav-tabs" style={{ marginBottom: '20px' }}>
        {[
          { key: 'dashboards', label: '📊 Custom Dashboards & Visual Analytics' },
          { key: 'trends', label: '📈 Statistical Trends (3-Sigma SPC)' },
          { key: 'spi_stats', label: '🎯 Safety Performance (SPI/SPT) Analytics & Governance' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* TAB 1: Custom Dashboards & Visual Analytics (Fully embedded AnalysisPage) */}
      {activeTab === 'dashboards' && (
        <div style={{ marginBottom: '32px' }}>
          <AnalysisPage />
        </div>
      )}

      {/* TAB 2: Statistical Trends & 3-Sigma SPC Chart */}
      {activeTab === 'trends' && (
        <div>
          {/* SPC Chart Card */}
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '20px',
              marginBottom: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  📈 3-Sigma Statistical Process Control Chart ({spcReport?.metric_name || 'Fleet Operational Occurrences'})
                </h3>
                <p style={{ fontSize: '12px', color: '#64748b', margin: '3px 0 0' }}>
                  ICAO Doc 9859 / EASA SPC: Baseline Mean: <strong>{spcReport?.mean ?? '-'}</strong> | Warning Limit (+2σ): <strong>{spcReport?.warning_limit ?? '-'}</strong> | Action Limit (+3σ): <strong>{spcReport?.action_limit ?? '-'}</strong> | Sigma (σ): <strong>{spcReport?.sigma ?? '-'}</strong> | Period: <strong>{spcReport?.period_start || '-'} → {spcReport?.period_end || '-'}</strong> ({spcReport?.sample_size ?? 0} months).
                </p>
              </div>

              {/* Metric & Window Selectors */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <select
                  value={spcMetricCode}
                  onChange={(e) => handleSpcMetricChange(e.target.value)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#1e293b',
                    background: '#f8fafc',
                  }}
                >
                  <option value="OCCURRENCES_ALL">📊 All Operational Occurrences (Fleet-wide)</option>
                  <option value="SPI-UNSTAB-APPR">🛫 Unstabilized Approach Rate (SPI-FLT-01)</option>
                  <option value="SPI-FLT-02">📡 TCAS Resolution Advisory Rate (SPI-FLT-02)</option>
                  <option value="SPI-RAMP-01">🛞 Ground & Ramp Contact Rate (SPI-RAMP-01)</option>
                </select>

                <select
                  value={spcMonths}
                  onChange={(e) => handleSpcMonthsChange(Number(e.target.value))}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#1e293b',
                    background: '#f8fafc',
                  }}
                >
                  <option value={12}>12 Months</option>
                  <option value={18}>18 Months</option>
                  <option value={24}>24 Months</option>
                  <option value={36}>36 Months</option>
                </select>

                <div style={{ display: 'flex', gap: '6px', fontSize: '11px', fontWeight: 600 }}>
                  <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: '#eff6ff', color: '#2563eb' }}>
                    Observed Rate
                  </span>
                  <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: '#fef3c7', color: '#b45309' }}>
                    Warning (+2σ)
                  </span>
                  <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: '#fee2e2', color: '#b91c1c' }}>
                    Action (+3σ)
                  </span>
                  {spcReport && spcReport.detected_anomalies > 0 && (
                    <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>
                      ⚠️ {spcReport.detected_anomalies} Anomaly Detected
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div style={{ width: '100%', height: '300px' }}>
              {spcLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#64748b', fontSize: '13px' }}>
                  ⏳ Calculating 3-Sigma statistical limits from database records...
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={290} minWidth={100} minHeight={200}>
                  <LineChart data={spcChartData} margin={{ top: 15, right: 25, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="period" stroke="#64748b" fontSize={11} tickLine={false} height={30} />
                    <YAxis
                      stroke="#64748b"
                      fontSize={11}
                      tickLine={false}
                      width={40}
                      domain={[0, Math.max(10, Math.ceil((spcReport?.action_limit || 8) * 1.3))]}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload as SpcDataPoint;
                          return (
                            <div
                              style={{
                                background: '#ffffff',
                                border: '1px solid #cbd5e1',
                                padding: '10px 14px',
                                borderRadius: '8px',
                                fontSize: '12px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              }}
                            >
                              <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>Period: {label}</div>
                              <div style={{ color: '#2563eb', fontWeight: 600 }}>Rate: {data.rate}</div>
                              <div style={{ color: '#64748b' }}>Events: {data.count} occurrence(s)</div>
                              <div style={{ marginTop: '6px', fontSize: '11px', fontWeight: 600 }}>
                                {data.alert_type === 'CRITICAL' ? (
                                  <span style={{ color: '#dc2626' }}>🚨 Exceeds Action Limit (+3σ: {data.ucl3})</span>
                                ) : data.alert_type === 'WARNING' ? (
                                  <span style={{ color: '#d97706' }}>⚠️ Exceeds Warning Limit (+2σ: {data.ucl2})</span>
                                ) : (
                                  <span style={{ color: '#16a34a' }}>✓ Within Statistical Control (Normal)</span>
                                )}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    {spcReport && (
                      <>
                        <ReferenceLine
                          y={spcReport.mean}
                          stroke="#64748b"
                          strokeDasharray="3 3"
                          label={{ value: `Mean: ${spcReport.mean}`, fill: '#64748b', fontSize: 10, position: 'right' }}
                        />
                        <ReferenceLine
                          y={spcReport.warning_limit}
                          stroke="#f59e0b"
                          strokeDasharray="4 4"
                          label={{ value: `+2σ: ${spcReport.warning_limit}`, fill: '#d97706', fontSize: 10, position: 'right' }}
                        />
                        <ReferenceLine
                          y={spcReport.action_limit}
                          stroke="#ef4444"
                          strokeDasharray="4 4"
                          label={{ value: `+3σ: ${spcReport.action_limit}`, fill: '#dc2626', fontSize: 10, position: 'right' }}
                        />
                      </>
                    )}
                    <Line
                      type="monotone"
                      dataKey="rate"
                      name="Observed Rate"
                      stroke="#2563eb"
                      strokeWidth={3}
                      dot={(props: any) => {
                        const { cx, cy, payload } = props;
                        const isCritical = payload?.alert_type === 'CRITICAL';
                        const isWarning = payload?.alert_type === 'WARNING';
                        const fillColor = isCritical ? '#dc2626' : isWarning ? '#d97706' : '#2563eb';
                        return (
                          <circle
                            key={`dot-${payload.period}`}
                            cx={cx}
                            cy={cy}
                            r={isCritical ? 6 : isWarning ? 5 : 4}
                            fill={fillColor}
                            stroke="#ffffff"
                            strokeWidth={2}
                          />
                        );
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Trend Events Table */}
          <div
            className="table-container"
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              marginBottom: '24px',
            }}
          >
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#0f172a', margin: 0 }}>
                🚨 Detected 3-Sigma Statistical Trend Events (RULE-12: Trend ≠ Causation)
              </h3>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <tr>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>Metric & Detection</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>Direction & Severity</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>Baseline / Current</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>Deviation %</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>Finding Summary</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>Status</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: '#475569', textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {trendEvents.map((te) => (
                  <tr key={te.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: '#0f172a' }}>
                      <div>{te.metric_name}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{te.detected_at.slice(0, 16)}</div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span
                        style={{
                          padding: '3px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: te.severity === 'CRITICAL' ? '#fee2e2' : '#ffedd5',
                          color: te.severity === 'CRITICAL' ? '#b91c1c' : '#c2410c',
                        }}
                      >
                        {te.direction} ({te.severity})
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#475569' }}>
                      {te.baseline_value} → <strong>{te.current_value}</strong>
                    </td>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: '#ea580c' }}>
                      +{te.deviation_pct}%
                    </td>
                    <td style={{ padding: '12px 16px', color: '#334155' }}>{te.summary_finding}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span
                        style={{
                          padding: '3px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: te.status === 'MITIGATED' ? '#dcfce7' : '#fef3c7',
                          color: te.status === 'MITIGATED' ? '#15803d' : '#b45309',
                        }}
                      >
                        {te.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      {te.status !== 'MITIGATED' && (
                        <button
                          onClick={() => handleReviewTrend(te.id, 'MITIGATED')}
                          className="btn-secondary"
                          style={{
                            padding: '4px 10px',
                            fontSize: '12px',
                            width: 'auto',
                            color: '#0d9488',
                            borderColor: '#99f6e4',
                          }}
                        >
                          Review & Close
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 3: Safety Performance (SPI/SPT) Statistics & Governance (Item 10) */}
      {activeTab === 'spi_stats' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Top SPI Analytics KPI Cards */}
          <div className="stats-grid">
            <div className="stat-card" style={{ borderLeft: '4px solid #2563eb' }}>
              <div className="stat-label">Total Registered SPIs</div>
              <div className="stat-value" style={{ color: '#0f172a' }}>
                {spiStats?.totalIndicators ?? '-'}
              </div>
              <div className="stat-sub">
                <span style={{ color: '#16a34a', fontWeight: 600 }}>{spiStats?.activeIndicators ?? 0} Active</span>
                {' • '}
                <span style={{ color: '#64748b' }}>{spiStats?.archivedIndicators ?? 0} Archived</span>
              </div>
            </div>

            <div className="stat-card" style={{ borderLeft: '4px solid #16a34a' }}>
              <div className="stat-label">Safety Compliance Rate</div>
              <div className="stat-value" style={{ color: '#16a34a' }}>
                {spiStats?.complianceSummary ? `${spiStats.complianceSummary.complianceRate}%` : '100%'}
              </div>
              <div className="stat-sub">
                {spiStats?.complianceSummary?.normalCount ?? 0} Indicators In Normal Bounds
              </div>
            </div>

            <div className="stat-card" style={{ borderLeft: '4px solid #d97706' }}>
              <div className="stat-label">Leading vs Lagging</div>
              <div className="stat-value" style={{ color: '#d97706' }}>
                {spiStats ? `${spiStats.leadingCount} / ${spiStats.laggingCount}` : '-'}
              </div>
              <div className="stat-sub">
                Precursor Prevention vs Outcome Metrics
              </div>
            </div>

            <div className="stat-card" style={{ borderLeft: '4px solid #dc2626' }}>
              <div className="stat-label">Active Limit Deviations</div>
              <div className="stat-value" style={{ color: '#dc2626' }}>
                {spiStats?.complianceSummary ? (spiStats.complianceSummary.alert1Count + spiStats.complianceSummary.alert2Count + spiStats.complianceSummary.alert3Count + spiStats.complianceSummary.breachCount) : 0}
              </div>
              <div className="stat-sub">
                {spiStats?.complianceSummary?.alert3Count ?? 0} Critical (3σ/Red) • {spiStats?.complianceSummary?.alert2Count ?? 0} Warning
              </div>
            </div>
          </div>

          {/* Row of Methodology and Cadence Breakdown Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
            {/* Cadence Breakdown */}
            <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', margin: '0 0 14px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>⏱️</span> Monitoring Cadence & Frequency
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#0369a1' }}>Monthly Frequency</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>12 recording periods per year</div>
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: 800, color: '#0284c7' }}>
                    {spiStats?.cadenceBreakdown?.monthly ?? 0}
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#fefce8', borderRadius: '8px', border: '1px solid #fef08a' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#854d0e' }}>Quarterly Frequency</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>4 quarterly cycles (Q1-Q4)</div>
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: 800, color: '#ca8a04' }}>
                    {spiStats?.cadenceBreakdown?.quarterly ?? 0}
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#faf5ff', borderRadius: '8px', border: '1px solid #e9d5ff' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#6b21a8' }}>Annual Frequency</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Year-end consolidated measurement</div>
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: 800, color: '#9333ea' }}>
                    {spiStats?.cadenceBreakdown?.annual ?? 0}
                  </div>
                </div>
              </div>
            </div>

            {/* Threshold Methodology Governance */}
            <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', margin: '0 0 14px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>📐</span> Alert Threshold Methodologies (Item 13)
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ padding: '12px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>📐 EASA 3-Sigma Statistical Method</span>
                    <span style={{ fontSize: '16px', fontWeight: 800, color: '#2563eb' }}>
                      {spiStats?.methodologyBreakdown?.threeSigma ?? 0}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: '11px', color: '#64748b', lineHeight: 1.4 }}>
                    Evaluates performance variations dynamically against historical baseline mean and standard deviations (1σ caution, 2σ warning, 3σ critical alarm).
                  </p>
                </div>

                <div style={{ padding: '12px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>🎯 Specific Target Limits</span>
                    <span style={{ fontSize: '16px', fontWeight: 800, color: '#7c3aed' }}>
                      {spiStats?.methodologyBreakdown?.specificTarget ?? 0}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: '11px', color: '#64748b', lineHeight: 1.4 }}>
                    Explicit numeric thresholds assigned per indicator: Warning Amber limit and Critical Red safety breach ceiling.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Departmental SPI Distribution & Missing Data Health Table */}
          <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  🏢 Departmental SPI Distribution & Data Health
                </h3>
                <p style={{ fontSize: '12px', color: '#64748b', margin: '3px 0 0' }}>
                  Cross-departmental ownership, data provider duties, and pending data entry counts
                </p>
              </div>
            </div>

            <table className="data-table">
              <thead>
                <tr>
                  <th>Department</th>
                  <th>Owned Indicators</th>
                  <th>Data Provider Roles</th>
                  <th>Pending Missing Entries</th>
                  <th>Status & Health</th>
                </tr>
              </thead>
              <tbody>
                {!spiStats?.departmentDistribution || spiStats.departmentDistribution.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                      No departmental distribution data available.
                    </td>
                  </tr>
                ) : (
                  spiStats.departmentDistribution.map((d) => (
                    <tr key={d.department}>
                      <td style={{ fontWeight: 700, color: '#0f172a' }}>
                        {d.department}
                      </td>
                      <td>
                        <span style={{ padding: '2px 8px', borderRadius: '4px', background: '#e0f2fe', color: '#0369a1', fontWeight: 700, fontSize: '12px' }}>
                          {d.ownedCount} Owned
                        </span>
                      </td>
                      <td>
                        <span style={{ padding: '2px 8px', borderRadius: '4px', background: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: '12px' }}>
                          {d.providerCount} Provider
                        </span>
                      </td>
                      <td>
                        {d.missingCount > 0 ? (
                          <span style={{ padding: '2px 8px', borderRadius: '4px', background: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '12px' }}>
                            ⚠️ {d.missingCount} Missing
                          </span>
                        ) : (
                          <span style={{ padding: '2px 8px', borderRadius: '4px', background: '#f0fdf4', color: '#166534', fontWeight: 600, fontSize: '12px' }}>
                            ✓ Up to date
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`badge-pill ${d.missingCount > 0 ? 'badge-warning' : 'badge-success'}`}>
                          {d.missingCount > 0 ? 'Data Entry Due' : 'Healthy'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
